
import { defineComponent, computed, toRefs, watch, ref } from "vue";
import AdmIcon from "../Icon";
import { errorIcon, isValidIcon } from "../config";
import { useId } from "../hooks/identifier";
import "./input.scss";

const Input = defineComponent({
  components: { AdmIcon },
  inheritAttrs: false,
  emits: ["update:modelValue", "clickTrailingIcon"],
  props: {
    id: {
      type: String,
      default: "",
      required: true,
    },
    forceId: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
    trailingIcon: {
      type: String,
      default: "",
    },
    description: {
      type: String,
      default: "",
    },
    error: {
      type: String,
      default: "",
    },
    isValid: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    formatter: {
      type: Function,
      default: undefined,
    },
  },
  setup(props, { emit }) {
    const {
      trailingIcon,
      error,
      isValid,
      modelValue,
      disabled,
      placeholder,
      description,
      label,
      formatter,
    } = toRefs(props);
    const inputRef = ref();
    const value = ref<string>(modelValue.value);
    const isFocused = ref(false);
    const identifier = useId(props, "adm-input");

    const trIcon = computed(() => {
      if (trailingIcon.value) {
        return trailingIcon.value;
      }

      if (disabled.value) {
        return null;
      }

      if (error.value) {
        return errorIcon;
      }

      if (isValid.value) {
        return isValidIcon;
      }

      return null;
    });

    watch(value, (nextValue) => {
      let formattedValue = nextValue;
      if (typeof formatter.value === "function") {
        formattedValue = formatter.value(nextValue);
        value.value = formattedValue;
      }

      emit("update:modelValue", formattedValue);
    });

    watch(modelValue, (nextModelValue) => {
      if (nextModelValue !== value.value) {
        value.value = nextModelValue;
      }
    });

    const onFocus = () => {
      isFocused.value = true;
    };

    const onBlur = () => {
      isFocused.value = false;
    };

    const classes = computed(() => ({
      "adm-input": true,
      "adm-input__focus": isFocused.value,
      "adm-input__disabled": disabled.value,
      "adm-input__valid": isValid.value,
      "adm-input__error": !!error.value,
      "adm-input__description": !!description.value,
      "adm-input__placeholder": !!placeholder.value,
      "adm-input__no-label": !label.value,
      "adm-input__trailing-icon": !!trIcon.value,
      "adm-input__trailing-text": !!description.value || !!error.value,
      "adm-input__floated-label":
        (!!placeholder.value ||
          isFocused.value ||
          value.value ||
          modelValue.value) &&
        !!label.value,
    }));

    const onClickLabel = () => {
      inputRef.value.focus();
    };

    return {
      identifier,
      trIcon,
      value,
      onFocus,
      onBlur,
      classes,
      inputRef,
      onClickLabel,
    };
  },
});

export default Input;
